import React, { useContext, useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import MicrogridTable from "./MicrogridTable";
import AddMicrogridModal from "./AddMicrogridModal";
import DeleteMicrogridModal from "./DeleteMicrogridModal";
import Breadcrumbs from "../../../components/Microgrid/BreadCrumbsFile";
import Button from "../../../components/Common/Button";
import { TableSkeleton } from "../../../components/Common/Table";
import { microgridTableColumns, MICROGRIDS_PAGE_TITLE } from "../dataFiles/microgridsPageDataFile";

import {
  fetchStorageAccountName,
  fetchMicrogridList,
  fetchMicrogridSysconfig,
} from "../api/microgridsApi";
import { fetchCustomersByBranch } from "../../users/api/customersApi";
import {
  customerPrefixFor,
  isListedFor,
  isDeletableFor,
} from "../domain/customerContainers";
import { mayManageMicrogrids } from "../domain/microgridRoles";
import { stripCustomerPrefix } from "../../simulations/utils/simulationCrumbs";
import { listDevicesFromSysconfig } from "../../devices/domain/deviceModel";
import { UserSessionContext } from "../../../session/UserSessionContext";
import { useNavigation } from "../../../contexts/NavigationContext";
import { useMicrogrid } from "../MicrogridContext";
import useAddMicrogrid from "../hooks/useAddMicrogrid";
import useDeleteMicrogrid from "../hooks/useDeleteMicrogrid";
import { onCopilotDataChanged } from "../../copilot/copilotEvents";

export default function MicrogridsPage() {
  const navigate = useNavigate();
  const { userSession } = useContext(UserSessionContext) || {};
  const {
    setSelectedMicrogrid: setNavMicrogrid,
    selectedMicrogrid,
    setStorageAccountName: setNavStorageAccount,
    selectedBranchId,
    selectedBranchName,
    selectedCustomerName,
  } = useNavigation();
  const { setSelectedDesign } = useMicrogrid();

  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [storageAccountName, setStorageAccountName] = useState(
    sessionStorage.getItem("storageAccountName"),
  );
  const [authContextStatus, setAuthContextStatus] = useState("loading");
  const [refreshList, setRefreshList] = useState(0);
  const isMountedRef = useRef(true);

  const accessToken =
    userSession?.auth?.accessToken || sessionStorage.getItem("accessToken");
  const userId =
    userSession?.user?.id || sessionStorage.getItem("UserID") || "";
  const jobTitle =
    userSession?.user?.jobTitle || sessionStorage.getItem("jobTitle");
  // The customer picked on the Customers page (for a Customer, set at
  // sign-in). With none picked, the list falls back to the signed-in user's
  // own email, as it always has, and offers no Delete.
  const selectedCustomerEmail = sessionStorage.getItem("selectedCustomerEmail");
  const userEmail = selectedCustomerEmail || sessionStorage.getItem("email");
  const customerPrefix = customerPrefixFor(userEmail);
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const isCustomer = jobTitle === "Customer";
  // An allowlist of job titles, so a missing or unknown one can't delete.
  const canManage = mayManageMicrogrids(jobTitle);
  const canDelete = canManage && !!selectedCustomerEmail;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // --- Grene AI changed something (copilot:data-changed) ---
  // After an agent's write or a finished run, refetch quietly: no skeleton
  // swap, so the rows stay and an open Add or Delete dialog survives. Only
  // this load is quiet; the first load and the page's own refreshes keep
  // the skeleton. A change announced while the skeleton shows, or once a
  // load has asked for it, loads again with it, so that load's errors still
  // show. Nothing announces it with the copilot off.
  const quietRefreshRef = useRef(false);
  // What's on screen: a layout effect, so it's current as soon as the table
  // or the skeleton is in the DOM (a passive effect can still be pending
  // when the event comes, and would quiet nothing).
  const loadingRef = useRef(loading);
  useLayoutEffect(() => {
    loadingRef.current = loading;
  }, [loading]);
  useEffect(
    () =>
      onCopilotDataChanged(() => {
        quietRefreshRef.current = !loadingRef.current;
        setRefreshList((n) => n + 1);
      }),
    [],
  );

  // --- The branch's customers ---
  // Their emails, from the same customers/by-branch call the Customers page
  // makes. One storage account holds every customer of the branch, and
  // customer prefixes can nest (bob@x.co's "bob-x-co" is the start of
  // bob@x.co.uk's "bob-x-co-uk"), so only the full list tells whose a
  // container is (see customerContainers.js).
  //
  // null while it loads and if it fails: the list then falls back to the "-"
  // boundary alone, which can show a nested customer's containers (bob@x.co
  // sees bob@x.co.uk's), so no row offers Delete until the list has loaded.
  // Only staff roles fetch it; a Customer's page is never sent the other
  // customers' emails, stays on that fallback, and has no Delete anyway.
  const [branchEmails, setBranchEmails] = useState(null);
  useEffect(() => {
    setBranchEmails(null);
    if (!canManage || !selectedBranchId || !accessToken) return;

    let cancelled = false;
    fetchCustomersByBranch({ accessToken, branchId: selectedBranchId, userId })
      .then((customers) => {
        if (!cancelled) {
          setBranchEmails(customers.map((c) => c?.email).filter(Boolean));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[MicrogridsPage] Branch customers fetch failed:", err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [canManage, selectedBranchId, accessToken, userId]);

  // --- Resolve storage account ---
  useEffect(() => {
    if (storageAccountName && baseUrl) {
      setAuthContextStatus("ready");
      return;
    }

    let cancelled = false;
    const resolve = async () => {
      setLoading(true);
      setAuthContextStatus("loading");
      try {
        const name = await fetchStorageAccountName({
          accessToken,
          branchId: selectedBranchId,
          userId,
        });
        if (!cancelled) {
          setNavStorageAccount(name);
          setStorageAccountName(name);
          setAuthContextStatus("ready");
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.status;
        if (status === 401) setAuthContextStatus("unauthenticated");
        else if (status === 403) setAuthContextStatus("forbidden");
        else if (status === 404) setAuthContextStatus("missing_storage");
        else setAuthContextStatus("internal_error");
        console.error("[Microgrid] Storage account fetch failed:", err);
        setLoading(false);
      }
    };
    resolve();
    return () => {
      cancelled = true;
    };
  }, [storageAccountName, baseUrl, accessToken, selectedBranchId, userId, setNavStorageAccount]);

  // --- Parse container data ---
  const parseContainerData = useCallback(
    (data) => {
      const containers = [];
      for (const container in data) {
        // The "-" boundary only; visibleDesigns below narrows this to the
        // customer's own containers once the branch's customers are known.
        // An empty prefix (no email in the session) keeps nothing.
        if (!isListedFor(container, userEmail, null)) continue;
        const allFiles = [
          ...(data[container].input || []),
          ...(data[container].sysconfig || []),
          ...(data[container].results || []),
          ...(data[container].other || []),
        ];
        if (!allFiles.length) continue;
        const createdTimes = allFiles.map((f) => new Date(f.created));
        const accessedTimes = allFiles.map((f) => new Date(f.last_accessed));
        const created = new Date(Math.min(...createdTimes));
        const lastAccessed = new Date(Math.max(...accessedTimes));
        containers.push({ name: container, created, lastAccessed });
      }
      containers.sort((a, b) => b.lastAccessed - a.lastAccessed);

      const fmt = (d) =>
        d.toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });

      return containers.map((c) => ({
        name: c.name,
        displayName: stripCustomerPrefix(c.name, customerPrefix),
        created: fmt(c.created),
        lastAccessed: fmt(c.lastAccessed),
      }));
    },
    [userEmail, customerPrefix],
  );

  // --- Main data fetch ---
  useEffect(() => {
    if (!storageAccountName || !baseUrl) return;

    let cancelled = false;
    const load = async () => {
      const quiet = quietRefreshRef.current;
      quietRefreshRef.current = false;
      if (!quiet) {
        // The skeleton is on its way: a change announced before React
        // commits it loads again loud, so this load's errors still show.
        loadingRef.current = true;
        setLoading(true);
        setError("");
      }

      try {
        if (!accessToken) throw new Error("No access token");

        const data = await fetchMicrogridList({
          storageAccountName,
          accessToken,
        });
        const containers = parseContainerData(data);
        if (containers.length === 0) {
          setDesigns([]);
          setLoading(false);
          return;
        }

        const results = await Promise.all(
          containers.map(async (c) => {
            try {
              const sysconfig = await fetchMicrogridSysconfig({
                storageAccountName,
                simulationName: c.name,
                accessToken,
              });
              const meterName = sysconfig?.Microgrid?.meters?.[0]?.name ?? null;
              const meterCount = sysconfig?.Microgrid?.meters?.length || 0;
              const deviceCount = meterCount + listDevicesFromSysconfig(sysconfig).length;
              return { ...c, meterName, deviceCount };
            } catch {
              return { ...c, meterName: null, deviceCount: 0 };
            }
          }),
        );

        if (!cancelled) {
          setDesigns(results);
        }
      } catch (err) {
        console.error("[MicrogridsPage] Error:", err);
        // A quiet refetch that fails keeps the rows it had.
        if (!cancelled && !quiet) {
          const status = err?.status || err?.response?.status;
          if (status === 401) setAuthContextStatus("unauthenticated");
          else if (status === 403) setAuthContextStatus("forbidden");
          else setError(err.message || "Unknown error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    storageAccountName,
    baseUrl,
    accessToken,
    refreshList,
    parseContainerData,
    setNavMicrogrid,
    setSelectedDesign,
  ]);

  // --- What the table shows ---
  // Each container under its longest customer prefix only, once the branch's
  // customers are known. A row is `deletable` only for a role that may delete,
  // with a customer picked, and when the container is that customer's alone;
  // before the branch's customers load, none is.
  const visibleDesigns = useMemo(
    () =>
      designs
        .filter((d) => isListedFor(d.name, userEmail, branchEmails))
        .map((d) => ({
          ...d,
          deletable: canDelete && isDeletableFor(d.name, userEmail, branchEmails),
        })),
    [designs, userEmail, branchEmails, canDelete],
  );

  // --- Add / edit microgrid hook ---
  const addMicrogrid = useAddMicrogrid({
    storageAccountName,
    accessToken,
    userId,
    customerPrefix,
    onSuccess: () => setRefreshList((n) => n + 1),
  });

  // --- Delete microgrid hook ---
  // A microgrid is only its storage container in the sim, so once the sim
  // has deleted it, what's left to clear is this page's own state.
  const handleMicrogridDeleted = useCallback(
    (deleted) => {
      // Drop the row now, and refetch so the list matches the sim again.
      setDesigns((prev) => prev.filter((d) => d.name !== deleted.name));
      setRefreshList((n) => n + 1);

      // Forget any selection that still names the deleted microgrid.
      if (selectedMicrogrid === deleted.name) {
        setNavMicrogrid(null);
        // The run keys written only for the selected microgrid: its opened
        // run's daily summary and name (SimulationResults) and its meter's
        // demand-charge flag (useDemandChargeData). meterName,
        // currentSimulationTimeKey and lastSelectedCreatedAt stay: only
        // HistoryImport writes them, for a run of any of the customer's
        // microgrids.
        sessionStorage.removeItem("dailySummaryFileName");
        sessionStorage.removeItem("simulationDisplayName");
        sessionStorage.removeItem("hasDemandCharge");
      }
      setSelectedDesign?.((prev) => (prev === deleted.name ? null : prev));
      // Written together by the add flow (useAddMicrogrid) and read by UploadFiles.
      if (sessionStorage.getItem("microgridbase") === deleted.name) {
        sessionStorage.removeItem("microgridbase");
        sessionStorage.removeItem("microgridName");
      }
    },
    [selectedMicrogrid, setNavMicrogrid, setSelectedDesign],
  );

  const deleteMicrogrid = useDeleteMicrogrid({
    storageAccountName,
    accessToken,
    userId,
    onDeleted: handleMicrogridDeleted,
  });

  // --- Row actions ---
  const handleRowClick = (design) => {
    setNavMicrogrid(design.name);
    setSelectedDesign?.(design.name);
  };

  const handleViewDevices = (design) => {
    setNavMicrogrid(design.name);
    setSelectedDesign?.(design.name);
    navigate("/device-config");
  };

  const handleViewSimulations = (design) => {
    setNavMicrogrid(design.name);
    setSelectedDesign?.(design.name);
    navigate("/microgrid/meter/simulations");
  };

  const handleEdit = (design) => {
    addMicrogrid.openEdit(design);
  };

  const handleDelete = (design) => {
    // The table offers Delete only on deletable rows; check again here, as
    // this is the last step before the confirmation that sends the request.
    if (!canDelete) return;
    if (!isDeletableFor(design.name, userEmail, branchEmails)) return;
    deleteMicrogrid.openDelete(design);
  };

  const skeletonToolbar = {
    left: (
      <div>
        <h2 className="wg-page-header">{MICROGRIDS_PAGE_TITLE}</h2>
      </div>
    ),
  };

  // --- Auth / loading guard renders ---
  if (authContextStatus === "loading" && loading) {
    return (
      <div className="microgrid-page">
        <TableSkeleton
          columns={microgridTableColumns}
          rows={3}
          toolbar={skeletonToolbar}
        />
      </div>
    );
  }

  if (authContextStatus === "unauthenticated") {
    return (
      <div className="microgrid-page">
        <h1 className="page-heading">Microgrid???</h1>
        <div className="no-data" style={{ marginTop: 40, textAlign: "center" }}>
          <p style={{ fontWeight: 500, marginBottom: 8, fontSize: "1.1em" }}>
            Session expired
          </p>
          <p style={{ color: "#666", marginBottom: 16 }}>
            Your session has expired. Please sign out and sign back in.
          </p>
        </div>
      </div>
    );
  }

  if (authContextStatus === "forbidden") {
    return (
      <div className="microgrid-page">
        <h1 className="page-heading">Microgrid</h1>
        <div className="no-data" style={{ marginTop: 40, textAlign: "center" }}>
          <p style={{ fontWeight: 500, marginBottom: 8, fontSize: "1.1em" }}>
            Access denied
          </p>
          <p style={{ color: "#666", marginBottom: 16 }}>
            You do not have permission to access this microgrid.
          </p>
          <Button variant="primary" onClick={() => navigate("/branchhomepage")}>
            Go to Branches
          </Button>
        </div>
      </div>
    );
  }

  if (authContextStatus === "missing_storage") {
    return (
      <div className="microgrid-page">
        <h1 className="page-heading">Microgrid</h1>
        <div className="no-data" style={{ marginTop: 40, textAlign: "center" }}>
          <p style={{ fontWeight: 500, marginBottom: 8, fontSize: "1.1em" }}>
            Storage account not found
          </p>
          <p style={{ color: "#666", marginBottom: 16 }}>
            The storage account for this branch could not be found. It may not be
            fully provisioned.
          </p>
          <Button variant="primary" onClick={() => navigate("/branchhomepage")}>
            Go to Branches
          </Button>
        </div>
      </div>
    );
  }

  if (authContextStatus === "internal_error") {
    return (
      <div className="microgrid-page">
        <h1 className="page-heading">Microgrid</h1>
        <div className="no-data" style={{ marginTop: 40, textAlign: "center" }}>
          <p style={{ fontWeight: 500, marginBottom: 8, fontSize: "1.1em" }}>
            Something went wrong
          </p>
          <p style={{ color: "#666", marginBottom: 16 }}>
            Something went wrong while authorizing access. Please try again or
            contact support.
          </p>
        </div>
      </div>
    );
  }

  // --- Breadcrumbs ---
  const breadcrumbs =
    jobTitle === "Customer" ? (
      <Breadcrumbs
        crumbs={[
          {
            label:
              sessionStorage.getItem("customerFirstName") || "Customer",
          },
        ]}
      />
    ) : (
      <Breadcrumbs
        crumbs={[
          ...(jobTitle === "Employee"
            ? [{ label: "Customer List", path: "/employeehomepage" }]
            : [
                { label: " Branches", path: "/branchhomepage" },
                {
                  label: "Customer List",
                  path: "/employees",
                  state: { activeTab: "customers" },
                },
              ]),
          {
            label:
              sessionStorage.getItem("customerFirstName") || "Customer",
          },
        ]}
      />
    );

  // --- Main render ---
  if (loading) {
    return (
      <div className="microgrid-page">
        <TableSkeleton
          columns={microgridTableColumns}
          rows={3}
          toolbar={{
            ...skeletonToolbar,
            right: !isCustomer && (
              <Button onClick={addMicrogrid.openModal}>+ New Microgrid</Button>
            ),
          }}
        />
      </div>
    );
  }

  return (
    <div className="microgrid-page">
      <MicrogridTable
        microgrids={visibleDesigns}
        branchName={selectedBranchName}
        customerName={selectedCustomerName}
        activeMicrogridName={selectedMicrogrid}
        onRowClick={handleRowClick}
        onViewDevices={handleViewDevices}
        onViewSimulations={handleViewSimulations}
        onEdit={handleEdit}
        onDelete={canDelete ? handleDelete : undefined}
        onAddMicrogrid={addMicrogrid.openModal}
        isCustomer={isCustomer}
      />

      <AddMicrogridModal
        open={addMicrogrid.showModal}
        step={addMicrogrid.step}
        microgridName={addMicrogrid.microgridName}
        setMicrogridName={addMicrogrid.setMicrogridName}
        formData={addMicrogrid.formData}
        errors={addMicrogrid.errors}
        submitError={addMicrogrid.submitError}
        isSubmitting={addMicrogrid.isSubmitting}
        uploading={addMicrogrid.uploading}
        uploadMessage={addMicrogrid.uploadMessage}
        isEditingLoading={addMicrogrid.isEditingLoading}
        existingPrimaryDemandCharge={addMicrogrid.existingPrimaryDemandCharge}
        onClose={addMicrogrid.closeModal}
        handleChange={addMicrogrid.handleChange}
        handleFileChange={addMicrogrid.handleFileChange}
        handleCreateMicrogrid={addMicrogrid.handleCreateMicrogrid}
        handleSaveMeterDetails={addMicrogrid.handleSaveMeterDetails}
      />

      <DeleteMicrogridModal
        open={!!deleteMicrogrid.pendingDelete}
        microgrid={deleteMicrogrid.pendingDelete}
        storageAccountName={storageAccountName}
        onConfirm={deleteMicrogrid.confirmDelete}
        onClose={deleteMicrogrid.closeDelete}
        loading={deleteMicrogrid.isDeleting}
      />
    </div>
  );
}
